import {
  Badge,
  Box,
  LinkProps,
  List,
  Stack,
  SvgIconProps,
  Typography,
} from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { Icons, MenuListItem } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'
import { getPlatform, useAppVersion } from '@/core/native/app'
export * from './fragments'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { config } from '@/config'
import { individualHideHelp, individualHideInquiry } from '@/core/individual'
import { useDeviceInfo } from '@/core/native/device'
import { filterHiddenItem } from '@/lib/array'
import { getHelpUrl } from '@/lib/helpUrl'
import { openIntercom, updateIntercom } from '@/lib/intercom'

export type State = {
  isMembershipPersonalInformationRequired: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onLogout: () => Promise<void>
}

type ListProps = {
  title: string
  href?: string
  Icon: (props: SvgIconProps) => JSX.Element
  component?: string | ReactNode
  target?: LinkProps['target']
  onClick?: () => void
  notificationText?: string
}

export const SettingsTemplate: Template<State, Action> = ({
  state,
  onLogout,
}) => {
  const { t } = useTranslation('settings')
  const { routerPages, routerNames } = useRouter()
  const { deviceInfo } = useDeviceInfo()
  const { user } = useCurrentUser()
  const { onClick } = useBrowserLink(getHelpUrl('appHelp'))

  const list = useMemo<ListProps[]>(
    () =>
      deviceInfo?.platform
        ? [
            {
              title: t('accountSetting'),
              href: routerPages.AccountSetting,
              Icon: Icons.User,
              notificationText: state.isMembershipPersonalInformationRequired
                ? t('accountSettingNotification')
                : '',
            },
            {
              title: t('paymentMethod'),
              hidden: deviceInfo?.platform !== 'web',
              href: routerPages.PaymentDetail,
              Icon: Icons.Wallet,
            },
            {
              title: t('pushNotification'),
              hidden: deviceInfo?.platform === 'web',
              href: routerPages.PushNotificationSettings,
              Icon: Icons.Notice,
            },
            {
              title: t('emailNotification'),
              href: routerPages.EmailNotificationSettings,
              Icon: Icons.Mail,
            },
            {
              title: t('autoFollowSettings'),
              hidden: config.applicationType === 'standalone',
              href: routerPages.AutoFollowSettings,
              Icon: Icons.Group,
            },
            {
              title: t('privacySettings'),
              href: routerPages.PrivacySettings,
              Icon: Icons.Key,
            },
            {
              title: t('languageSetting'),
              href: routerPages.LanguageSetting,
              Icon: Icons.Language,
            },
            {
              title: t('guideUser'),
              Icon: Icons.Help,
              hidden: individualHideHelp(),
              onClick,
            },
            {
              title: t('inquiry'),
              Icon: Icons.Inquiry,
              hidden: individualHideInquiry(),
              onClick: () => {
                updateIntercom(user)
                openIntercom()
              },
            },
          ]
        : [],
    [
      deviceInfo?.platform,
      t,
      routerPages.AccountSetting,
      routerPages.PaymentDetail,
      routerPages.PushNotificationSettings,
      routerPages.EmailNotificationSettings,
      routerPages.AutoFollowSettings,
      routerPages.PrivacySettings,
      routerPages.LanguageSetting,
      state.isMembershipPersonalInformationRequired,
      onClick,
      user,
    ]
  )

  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,
      }}
    >
      <Head title={routerNames.Setting} />
      <List sx={{ bgcolor: 'grey.50' }}>
        {filterHiddenItem(list).map((item, index) => (
          <Box position="relative" key={index}>
            <MenuListItem
              divider
              title={item.title}
              href={item.href}
              target={item.target}
              icon={<item.Icon />}
              onClick={item.onClick}
              primaryTypographyProps={{
                variant: 'body1',
              }}
              secondaryAction={
                <Typography variant="overline" color="like.tap">
                  {item.notificationText}
                </Typography>
              }
              sx={{
                backgroundColor: 'grey.50',
                py: 2,
                px: 5,
                width: '100%',
                height: { xs: 52, md: 56 },
              }}
            />
            {item.notificationText && (
              <Badge
                badgeContent=""
                color={'like' as any}
                sx={{
                  position: 'absolute',
                  top: '18px',
                  left: '32px',
                  width: 12,
                  height: 12,
                  transform: 'scale(0.9)',
                  '& > span': {
                    minWidth: 12,
                    height: 12,
                    bgcolor: 'like.tap',
                  },
                }}
              />
            )}
          </Box>
        ))}
        <MenuListItem
          component={Box}
          divider
          title={t('signOut')}
          onClick={onLogout}
          icon={<Icons.Logout />}
          primaryTypographyProps={{
            variant: 'body1',
          }}
          sx={{
            backgroundColor: 'grey.50',
            py: 2,
            px: 5,
            width: '100%',
            height: 'auto',
          }}
        />
        {['development', 'staging'].includes(config.environment) && (
          <a href="https://forms.clickup.com/f/vpuz-1425/QA13X4VVW4QLB835KT">
            不具合
          </a>
        )}
      </List>
      <VersionCell />
    </BaseLayout>
  )
}

/**
 *
 * @description VersionCell
 * アプリのバージョン + コミットハッシュ表示
 * iOSの場合 CFBundleShortVersionStringを、Androidの場合はパッケージのversionNameを表示する。
 * stg/devの場合は環境名も表示する
 * webの場合は非表示
 */
const VersionCell = () => {
  const { t } = useTranslation('settings')
  const { buildId, releaseVersion } = config
  const appVersion = useAppVersion()
  const isWeb = getPlatform() === 'web'

  return (
    <CellFrame sx={{ py: 5 }}>
      <Stack justifyContent="center">
        {/* 本番環境でなければ、現在の環境名を表示 */}
        <Typography variant="subtitle2" align="center">
          {config.environment !== 'production' && ` ${config.environment}`}
        </Typography>
        {/* JSビルドを行ったコミットのハッシュ値6桁を表示 */}
        <Typography
          color="text.hint"
          variant="caption"
          align="center"
          sx={{ userSelect: 'text' }}
        >
          {`${t('version')} ${releaseVersion} ${
            buildId && `(${buildId.slice(0, 6)})`
          }`}
        </Typography>
      </Stack>
      {/* ネイティブの場合、ビルド番号を表示、この値はストアでも参照できる。 */}
      {!isWeb && (
        <Stack justifyContent="center">
          <Typography
            color="text.hint"
            variant="caption"
            align="center"
            sx={{ userSelect: 'text' }}
          >
            {appVersion}
          </Typography>
        </Stack>
      )}
    </CellFrame>
  )
}
